import { Fragment, useState, useEffect, useMemo, useCallback } from 'react';
import { Modal } from '@/components/ui/modal';
import { FormField as Field } from '@/components/ui/form-field';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { GuestManager } from '@/components/ui/guest-manager';
import AutocompleteSelect from '@/components/ui/autocomplete-select';
import { COUNTRIES } from '@/constants/countries';
import { LANGUAGE_LABELS, type AppLanguage } from '@/constants/languages';
import { TRAVEL_INTERESTS } from '@/constants/travel-interests';
import { useI18n } from '@/hooks/useI18n';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/hooks/useToast';
import {
  User, Mail, Phone, Users, MapPin, Euro, Tag, UserPlus, UserCheck,
  Briefcase, AlertCircle, Plane, Lock, Globe, SlidersHorizontal, X, Plus, Minus, Calendar, StickyNote,
} from 'lucide-react';
import { cn } from '@/components/utils/utils';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import type { DashboardTravelRequest } from '@/lib/models/dashboard';
import type { TranslationKey } from '@/types/i18n.types';
import {
  ADVISOR_SELF,
  BUDGET_CAP,
  FIELD_TAB,
  emptyClientRequestForm,
  hasTravellerData,
  toCreatePayload,
  toFormState,
  toUpdatePayload,
  validateClientRequestForm,
  type ClientRequestFormState,
  type ClientRequestTab,
} from './models/client-request-form.model';
import { useClientRequestActions } from './hooks/useClientRequestActions';
import { EdgeFunctionError } from '@/utils/supabase-functions';

/**
 * I tab con almeno un campo validato sono quelli che bisogna compilare per
 * salvare: si ricavano da FIELD_TAB invece di essere elencati a mano, cosi' un
 * campo obbligatorio in piu' rende obbligatorio il suo tab senza altre modifiche.
 */
const REQUIRED_TABS = new Set<ClientRequestTab>(Object.values(FIELD_TAB));

/** Stessa lista e stesso formato (alpha-2 minuscolo) della ricerca RateHawk. */
const RESIDENCY_OPTIONS = COUNTRIES.map((c) => ({
  value: c.code.toLowerCase(),
  label: c.name,
  icon: c.flag,
}));

interface ClientRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** 'create' apre il form vuoto, 'edit' lo apre sulla richiesta passata. */
  mode?: 'create' | 'edit';
  /** Obbligatoria in modalita' 'edit'. */
  request?: DashboardTravelRequest;
  /**
   * Tab su cui aprire il modale. Serve a chi ci arriva per un motivo preciso --
   * l'agency che deve assegnare la richiesta -- e non per modificarla in generale.
   * Ignorato se quel tab non e' disponibile per il ruolo.
   */
  initialTab?: ClientRequestTab;
}

export const ClientRequestModal = ({
  isOpen,
  onClose,
  onSuccess,
  mode = 'create',
  request,
  initialTab,
}: ClientRequestModalProps) => {
  const { t } = useI18n();
  const { userRole, user } = useAuth();
  const isEdit = mode === 'edit';
  const isAgency = userRole === 'agency';
  const isAdmin = userRole === 'admin';

  const { advisors, clients, loadAdvisors, loadClients, createRequest, updateRequest } =
    useClientRequestActions();

  const [form, setForm] = useState<ClientRequestFormState>(emptyClientRequestForm);
  const [activeTab, setActiveTab] = useState<ClientRequestTab>('client');
  const [showErrors, setShowErrors] = useState(false);
  const [customInterest, setCustomInterest] = useState('');
  const [isAddingInterest, setIsAddingInterest] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // In modifica il perimetro di scrittura e' quello della edge function: le
  // richieste chiuse sono in sola lettura per tutti tranne l'admin.
  const isClosed = isEdit && !!request && ['completed', 'rejected'].includes(request.status);
  const readOnly = isClosed && !isAdmin;
  const disabled = isLoading || readOnly;

  // Solo agency e admin possono spostare la richiesta su un altro advisor.
  const canAssign = isAgency || isAdmin;

  const patch = useCallback((changes: Partial<ClientRequestFormState>) => {
    setForm((prev) => ({ ...prev, ...changes }));
  }, []);

  // ── Precaricamento ────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const next = isEdit && request ? toFormState(request) : emptyClientRequestForm();
    setForm(next);
    setActiveTab(initialTab && (initialTab !== 'assignment' || canAssign) ? initialTab : 'client');
    setShowErrors(false);
    setCustomInterest('');
    setIsAddingInterest(false);
  }, [isOpen, isEdit, request, initialTab, canAssign]);

  useEffect(() => {
    if (!isOpen || !canAssign) return;
    loadAdvisors().catch(() => {
      toast({ title: t('common.error'), description: t('clientRequest.errors.advisorsLoad'), variant: 'destructive' });
    });
  }, [isOpen, canAssign, loadAdvisors, t]);

  useEffect(() => {
    if (!isOpen || isEdit || form.clientMode !== 'existing') return;
    loadClients().catch(() => {
      toast({ title: t('common.error'), description: t('clientRequest.errors.clientsLoad'), variant: 'destructive' });
    });
  }, [isOpen, isEdit, form.clientMode, loadClients, t]);

  // ── Validazione ───────────────────────────────────────────────────────
  const errors = useMemo(
    () => validateClientRequestForm(form, isEdit ? 'edit' : 'create'),
    [form, isEdit],
  );
  const errorOf = (field: string) =>
    showErrors && errors[field] ? String(t(errors[field] as TranslationKey)) : undefined;
  const errorCount = Object.keys(errors).length;

  const tabHasError = useCallback(
    (tab: ClientRequestTab) =>
      showErrors && Object.keys(errors).some((field) => FIELD_TAB[field] === tab),
    [errors, showErrors],
  );

  // Una voce selezionata combacia con una categoria per id oppure per etichetta:
  // le richieste piu' vecchie hanno salvato l'etichetta, non l'id.
  const matchPreset = useCallback((value: string) =>
    TRAVEL_INTERESTS.find((preset) =>
      preset.id.toLowerCase() === value.toLowerCase()
      || String(t(preset.labelKey as TranslationKey)).toLowerCase() === value.toLowerCase()),
  [t]);

  const availableInterests = useMemo(
    () => TRAVEL_INTERESTS.filter((preset) => !form.interests.some((value) =>
      preset.id.toLowerCase() === value.toLowerCase()
      || String(t(preset.labelKey as TranslationKey)).toLowerCase() === value.toLowerCase())),
    [form.interests, t],
  );

  const tabs = useMemo(() => {
    const list: { id: ClientRequestTab; label: string; icon: React.ReactNode }[] = [
      { id: 'client', label: String(t('clientRequest.tabs.client')), icon: <User className="h-4 w-4" /> },
      { id: 'trip', label: String(t('clientRequest.tabs.trip')), icon: <Plane className="h-4 w-4" /> },
      { id: 'travellers', label: String(t('clientRequest.tabs.travellers')), icon: <Users className="h-4 w-4" /> },
      { id: 'details', label: String(t('clientRequest.tabs.details')), icon: <SlidersHorizontal className="h-4 w-4" /> },
      { id: 'notes', label: String(t('clientRequest.tabs.notes')), icon: <StickyNote className="h-4 w-4" /> },
    ];
    if (canAssign) {
      list.push({ id: 'assignment', label: String(t('clientRequest.tabs.assignment')), icon: <Briefcase className="h-4 w-4" /> });
    }
    return list;
  }, [canAssign, t]);

  const firstOptional = tabs.findIndex((tab) => !REQUIRED_TABS.has(tab.id));
  const dividerAt = firstOptional > 0 ? firstOptional : -1;

  // ── Ospiti ────────────────────────────────────────────────────────────
  // Il numero di viaggiatori si imposta una volta sola, nel tab Viaggio: qui si
  // materializza una casella eta' per ciascuno. Aprendo il tab Dettagli la lista
  // si allinea al totale, cosi' i due numeri non possono divergere.
  const guestCount = Number(form.totalGuests) || 0;

  useEffect(() => {
    if (activeTab !== 'travellers' || readOnly) return;
    setForm((prev) => {
      const count = Number(prev.totalGuests) || 0;
      if (prev.guests.length === count) return prev;
      if (prev.guests.length > count) return { ...prev, guests: prev.guests.slice(0, count) };
      const guests = [...prev.guests];
      let nextId = guests.reduce((max, g) => Math.max(max, g.id), 0);
      while (guests.length < count) guests.push({ id: ++nextId, age: 25 });
      return { ...prev, guests };
    });
  }, [activeTab, form.totalGuests, readOnly]);

  // Le notti vengono dalle date, e sono l'unita' con cui si legge un budget
  // alberghiero: due settimane a 3.000 sono un viaggio diverso da tre notti a 3.000.
  const nights = useMemo(() => {
    if (!form.startDate || !form.endDate) return 0;
    const ms = new Date(form.endDate).getTime() - new Date(form.startDate).getTime();
    return ms > 0 ? Math.round(ms / 86400000) : 0;
  }, [form.startDate, form.endDate]);

  // Il budget si inserisce a persona per tutto il viaggio: da quel numero, piu' i
  // viaggiatori e le notti, si ricavano tutti i tagli con cui si ragiona davvero.
  // Ogni voce esiste solo se ha i suoi ingredienti.
  const budgetBreakdown = useMemo(() => {
    const min = Number(form.budgetMin) || 0;
    const max = Number(form.budgetMax) || 0;
    if (!min && !max) return [];

    const money = (n: number) => `${Math.round(n).toLocaleString('it-IT')} €`;
    const range = (transform: (n: number) => number) =>
      min && max ? `${money(transform(min))} – ${money(transform(max))}` : money(transform(min || max));

    const rows: { labelKey: string; value: string }[] = [
      { labelKey: 'clientRequest.trip.calcPerPerson', value: range((n) => n) },
    ];
    if (guestCount) {
      rows.push({ labelKey: 'clientRequest.trip.calcTotal', value: range((n) => n * guestCount) });
    }
    if (nights) {
      rows.push({ labelKey: 'clientRequest.trip.calcPerNightPerPerson', value: range((n) => n / nights) });
    }
    if (nights && guestCount) {
      rows.push({ labelKey: 'clientRequest.trip.calcPerNight', value: range((n) => (n * guestCount) / nights) });
    }
    return rows;
  }, [form.budgetMin, form.budgetMax, guestCount, nights]);

  // Voce scritta a mano: niente doppioni, ne' con le altre voci libere ne' con le
  // etichette delle categorie gia' scelte.
  const addCustomInterest = () => {
    const value = customInterest.trim();
    setCustomInterest('');
    setIsAddingInterest(false);
    if (!value) return;
    // Se quello che ha scritto e' una delle categorie, si salva come tale invece
    // di creare una voce libera identica a un chip che esiste gia'.
    const preset = matchPreset(value);
    const toAdd = preset ? preset.id : value;
    const already = form.interests.some((i) =>
      i.toLowerCase() === toAdd.toLowerCase()
      || (preset && i.toLowerCase() === String(t(preset.labelKey as TranslationKey)).toLowerCase()));
    if (!already) patch({ interests: [...form.interests, toAdd] });
  };

  const updateGuestAge = (guestId: number, age: number) => {
    patch({ guests: form.guests.map((g) => (g.id === guestId ? { ...g, age } : g)) });
  };

  // ── Submit ────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setShowErrors(true);
    if (errorCount > 0) {
      const firstTab = FIELD_TAB[Object.keys(errors)[0]];
      if (firstTab) setActiveTab(firstTab);
      return;
    }

    setIsLoading(true);
    try {
      if (isEdit && request) {
        await updateRequest(toUpdatePayload(form, request.id, canAssign, user?.id));
        toast({
          variant: 'success',
          title: String(t('common.success')),
          description: String(t('clientRequest.toast.updated')),
        });
      } else {
        const existingClient = clients.find((c) => c.id === form.selectedClientId);
        await createRequest(toCreatePayload(form, isAgency, existingClient && {
          firstName: existingClient.first_name,
          lastName: existingClient.last_name,
          email: existingClient.email,
          phone: existingClient.phone,
        }));
        toast({
          variant: 'success',
          title: String(t('common.success')),
          description: String(t('clientRequest.toast.created')),
        });
      }
      onSuccess?.();
      onClose();
    } catch (error) {
      // Email di un consulente, di un'agenzia o di un admin: mai come cliente
      const isStaffEmail = error instanceof EdgeFunctionError && error.code === 'client_email_not_traveler';
      toast({
        title: String(t('common.error')),
        description: isStaffEmail
          ? String(t('clientRequest.errors.clientEmailNotTraveler'))
          : (error as Error).message || String(t('errors.generic')),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) onClose();
  };

  // ── Sezioni ───────────────────────────────────────────────────────────
  const clientTab = (
    <div className="space-y-5">
      {/* Il toggle nuovo/esistente esiste solo in creazione: in modifica il
          cliente e' quello della richiesta, si modificano i suoi dati. */}
      {!isEdit && (
        <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
          {([
            { id: 'new' as const, label: t('clientRequest.client.newClient'), icon: <UserPlus className="h-4 w-4" /> },
            { id: 'existing' as const, label: t('clientRequest.client.existingClient'), icon: <UserCheck className="h-4 w-4" /> },
          ]).map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => patch({
                clientMode: option.id,
                ...(option.id === 'new'
                  ? { selectedClientId: '' }
                  : { firstName: '', lastName: '', email: '', phone: '' }),
              })}
              className={cn(
                'flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all',
                form.clientMode === option.id
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900',
              )}
            >
              {option.icon}
              {option.label}
            </button>
          ))}
        </div>
      )}

      {!isEdit && form.clientMode === 'existing' ? (
        <Field
          id="existingClient"
          label={String(t('clientRequest.client.selectClient'))}
          required
          error={errorOf('selectedClientId')}
        >
          <Select
            value={form.selectedClientId}
            onValueChange={(value) => patch({ selectedClientId: value })}
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder={String(t('clientRequest.client.selectClientPlaceholder'))} />
            </SelectTrigger>
            <SelectContent>
              {clients.map((client) => (
                <SelectItem key={client.id} value={client.id}>
                  {client.first_name} {client.last_name} ({client.email})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field id="firstName" label={String(t('clientRequest.client.firstName'))} required error={errorOf('firstName')}>
              <Input
                id="firstName"
                value={form.firstName}
                onChange={(e) => patch({ firstName: e.target.value })}
                placeholder="Mario"
                disabled={disabled}
                className="h-9"
              />
            </Field>
            <Field id="lastName" label={String(t('clientRequest.client.lastName'))} required error={errorOf('lastName')}>
              <Input
                id="lastName"
                value={form.lastName}
                onChange={(e) => patch({ lastName: e.target.value })}
                placeholder="Rossi"
                disabled={disabled}
                className="h-9"
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field
              id="email"
              label={String(t('clientRequest.client.email'))}
              icon={<Mail className="h-3 w-3" />}
              required
              error={errorOf('email')}
            >
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => patch({ email: e.target.value })}
                placeholder="mario.rossi@example.com"
                disabled={disabled}
                className="h-9"
              />
            </Field>
            <Field
              id="phone"
              label={String(t('clientRequest.client.phone'))}
              icon={<Phone className="h-3 w-3" />}
              required={!isEdit}
              error={errorOf('phone')}
            >
              <Input
                id="phone"
                type="tel"
                value={form.phone}
                onChange={(e) => patch({ phone: e.target.value })}
                placeholder="+39 123 456 7890"
                disabled={disabled}
                className="h-9"
              />
            </Field>
          </div>

          {/* Lingua del cliente: solo in creazione. Su un cliente che esiste
              gia' non si sovrascrive la lingua che ha impostato lui — per un
              singolo invio la si cambia dalla modale della proposta. */}
          {!isEdit && (
            <Field
              id="preferredLanguage"
              label={String(t('clientRequest.client.language'))}
              icon={<Globe className="h-3 w-3" />}
              hint={String(t('clientRequest.client.languageHint'))}
            >
              <Select
                value={form.preferredLanguage}
                onValueChange={(value) => patch({ preferredLanguage: value as AppLanguage })}
                disabled={disabled}
              >
                <SelectTrigger id="preferredLanguage" className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="it">{LANGUAGE_LABELS.it}</SelectItem>
                  <SelectItem value="en">{LANGUAGE_LABELS.en}</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          )}

          {isEdit && request?.userId && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
              {t('clientRequest.client.propagateNotice')}
            </p>
          )}

          {!isEdit && (
            <p className="text-xs text-gray-500 italic">
              {t('clientRequest.client.noEmailNotice')}
            </p>
          )}
        </>
      )}
    </div>
  );

  const tripTab = (
    <div className="space-y-5">
      <Field
        id="destinations"
        label={String(t('clientRequest.trip.destinations'))}
        icon={<MapPin className="h-3 w-3" />}
        required
        error={errorOf('destinations')}
        hint={String(t('clientRequest.trip.commaSeparated'))}
      >
        <Input
          id="destinations"
          value={form.destinations}
          onChange={(e) => patch({ destinations: e.target.value })}
          placeholder="Roma, Venezia, Firenze"
          disabled={disabled}
          className="h-9"
        />
      </Field>

      {/* Viaggiatori, date e budget sulla stessa riga. Date e budget hanno la
          stessa forma — una cella riquadrata con i due estremi impilati — perche'
          sono la stessa cosa: un intervallo, non due campi indipendenti. */}
      <div className="grid grid-cols-1 sm:grid-cols-[auto_minmax(0,18rem)_minmax(0,18rem)] gap-3 sm:justify-start">
        <div className="space-y-1.5">
          <Label className="text-xs flex items-center gap-1 text-gray-700">
            <Users className="h-3 w-3" />
            {t('clientRequest.trip.guests')}
          </Label>
          <div className="flex h-9 items-center justify-between rounded-md border border-input sm:w-28">
            <button
              type="button"
              disabled={disabled || guestCount < 1}
              onClick={() => patch({ totalGuests: guestCount > 1 ? String(guestCount - 1) : '' })}
              className="flex h-full w-9 items-center justify-center text-gray-500 hover:text-gray-900 disabled:opacity-30"
              aria-label={String(t('common.remove'))}
            >
              <Minus className="h-3.5 w-3.5" />
            </button>
            <span className="text-sm font-medium tabular-nums">{form.totalGuests || '–'}</span>
            <button
              type="button"
              disabled={disabled || guestCount >= 16}
              onClick={() => patch({ totalGuests: String(guestCount + 1) })}
              className="flex h-full w-9 items-center justify-center text-gray-500 hover:text-gray-900 disabled:opacity-30"
              aria-label={String(t('common.add'))}
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        <div className="space-y-1.5">
          <div className="flex items-baseline justify-between gap-2">
            <Label className="text-xs flex items-center gap-1 text-gray-700">
              <Calendar className="h-3 w-3" />
              {t('clientRequest.trip.datesSection')}
            </Label>
            {errorOf('endDate') ? (
              <span className="text-[11px] text-red-600">{errorOf('endDate')}</span>
            ) : nights > 0 ? (
              <span className="text-[11px] text-gray-500">
                {t('clientRequest.trip.nights', { count: nights })}
              </span>
            ) : null}
          </div>
          <div className="divide-y divide-gray-200 rounded-md border border-input">
            {([
              { id: 'startDate' as const, label: t('clientRequest.trip.rangeFrom') },
              { id: 'endDate' as const, label: t('clientRequest.trip.rangeTo') },
            ]).map((row) => (
              <div key={row.id} className="flex h-9 items-center gap-1.5 px-2.5">
                <label htmlFor={row.id} className="w-8 shrink-0 text-[11px] text-gray-500">
                  {row.label}
                </label>
                <input
                  id={row.id}
                  type="date"
                  value={row.id === 'startDate' ? form.startDate : form.endDate}
                  onChange={(e) => patch({ [row.id]: e.target.value } as Partial<ClientRequestFormState>)}
                  disabled={disabled}
                  className="w-full bg-transparent text-sm outline-none disabled:opacity-50"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs flex items-center gap-1 text-gray-700">
            <Euro className="h-3 w-3" />
            {t('clientRequest.trip.budgetPerPersonNote')}
          </Label>
          <div className="divide-y divide-gray-200 rounded-md border border-input">
            {([
              { id: 'budgetMin' as const, label: t('clientRequest.trip.rangeFrom'), placeholder: '1.000' },
              { id: 'budgetMax' as const, label: t('clientRequest.trip.rangeTo'), placeholder: '3.000' },
            ]).map((row) => (
              <div key={row.id} className="flex h-9 items-center gap-1.5 px-2.5">
                <label htmlFor={row.id} className="w-8 shrink-0 text-[11px] text-gray-500">
                  {row.label}
                </label>
                <span className="text-sm text-gray-400">€</span>
                <input
                  id={row.id}
                  type="number"
                  min="0"
                  max={BUDGET_CAP}
                  step="100"
                  value={row.id === 'budgetMin' ? form.budgetMin : form.budgetMax}
                  onChange={(e) => patch({ [row.id]: e.target.value } as Partial<ClientRequestFormState>)}
                  placeholder={row.placeholder}
                  disabled={disabled}
                  className="w-full bg-transparent text-sm tabular-nums outline-none disabled:opacity-50"
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Prospetto dei conti ricavabili: ogni voce compare solo quando ci sono i
          dati per calcolarla. Col solo budget si vede la riga a persona; con i
          viaggiatori arriva il totale, con le date il conto a notte. */}
      {(errorOf('budgetMax') || errorOf('totalGuests') || budgetBreakdown.length > 0) && (
        <div className="max-w-3xl rounded-md bg-gray-50 px-3 py-2.5">
          {errorOf('budgetMax') || errorOf('totalGuests') ? (
            <span className="text-[11px] text-red-600 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              {errorOf('budgetMax') || errorOf('totalGuests')}
            </span>
          ) : (
            <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {budgetBreakdown.map((row) => (
                <div key={row.labelKey}>
                  <dt className="text-[10px] uppercase tracking-wide text-gray-400">
                    {t(row.labelKey as TranslationKey)}
                  </dt>
                  <dd className="text-sm text-gray-900 tabular-nums">{row.value}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      )}

    </div>
  );

  const travellersTab = (
    <div className="space-y-5">
      <p className="text-xs text-gray-500">
        {t('clientRequest.travellers.intro')}
      </p>

      <Field
        id="residency"
        label={String(t('clientRequest.travellers.residency'))}
        icon={<Globe className="h-3 w-3" />}
        hint={String(t('clientRequest.travellers.residencyHint'))}
      >
        <AutocompleteSelect
          options={RESIDENCY_OPTIONS}
          value={form.residency}
          onValueChange={(value) => patch({ residency: value })}
          placeholder={String(t('clientRequest.travellers.residencyPlaceholder'))}
          searchPlaceholder={String(t('ratehawk.searchNationality'))}
          filterMode="includes"
          maxHeight="200px"
          disabled={disabled}
        />
      </Field>

      <div className="space-y-2">
        <Label className="text-xs text-gray-700">{t('clientRequest.trip.guestsAges')}</Label>
        {guestCount > 0 ? (
          <GuestManager
            totalGuests={guestCount}
            guests={form.guests}
            onAddGuest={() => undefined}
            onRemoveGuest={() => undefined}
            onUpdateGuestAge={updateGuestAge}
            disabled={disabled}
            readOnlyCount
          />
        ) : (
          <p className="text-xs text-gray-500">{t('clientRequest.travellers.noGuests')}</p>
        )}
      </div>
    </div>
  );

  const detailsTab = (
    <div className="space-y-5">
      <p className="text-xs text-gray-500">
        {t('clientRequest.details.intro')}
      </p>

      <Field
        id="departurePoint"
        label={String(t('clientRequest.trip.departurePoint'))}
        icon={<MapPin className="h-3 w-3" />}
      >
        <Input
          id="departurePoint"
          value={form.departurePoint}
          onChange={(e) => patch({ departurePoint: e.target.value })}
          placeholder="Milano"
          disabled={disabled}
          className="h-9"
        />
      </Field>

      {/* Tre sì/no brevi: una riga, non tre. */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {([
          { key: 'needsTransportation' as const, label: t('clientRequest.trip.needsTransportation') },
          { key: 'travelDatesFlexible' as const, label: t('clientRequest.trip.datesFlexible') },
          { key: 'budgetFlexible' as const, label: t('clientRequest.trip.budgetFlexible') },
        ]).map((row) => (
          <div key={row.key} className="flex h-9 items-center justify-between gap-2 rounded-md border border-gray-200 px-2.5">
            <Label htmlFor={row.key} className="text-[11px] leading-tight text-gray-700">{row.label}</Label>
            <Switch
              id={row.key}
              checked={form[row.key]}
              onCheckedChange={(checked) => patch({ [row.key]: checked } as Partial<ClientRequestFormState>)}
              disabled={disabled}
            />
          </div>
        ))}
      </div>

      {/* Interessi: categorie nostre come scorciatoia, ma la lista non e' chiusa —
          quello che il cliente chiede raramente sta in quindici etichette. Le voci
          scritte a mano restano tali e quali, a DB convivono con gli id. */}
      <div className="space-y-2">
        <Label className="text-xs flex items-center gap-1 text-gray-700">
          <Tag className="h-3 w-3" />
          {t('clientRequest.trip.interests')}
          <span className="text-gray-400 font-normal">· {t('clientRequest.optional')}</span>
        </Label>

        {form.interests.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {form.interests.map((interest) => {
              const preset = matchPreset(interest);
              const Icon = preset?.icon;
              return (
                <span
                  key={interest}
                  className="inline-flex items-center gap-1.5 rounded-full bg-gray-900 px-2.5 py-1 text-[11px] text-white"
                >
                  {Icon && <Icon className="h-3 w-3" />}
                  {preset ? t(preset.labelKey as TranslationKey) : interest}
                  {!disabled && (
                    <button
                      type="button"
                      onClick={() => patch({ interests: form.interests.filter((i) => i !== interest) })}
                      className="text-white/60 hover:text-white"
                      aria-label={String(t('common.remove'))}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </span>
              );
            })}
          </div>
        )}

        {!disabled && (
          <>
            <div className="flex flex-wrap gap-1.5">
              {availableInterests.map((preset) => {
                const Icon = preset.icon;
                return (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => patch({ interests: [...form.interests, preset.id] })}
                    className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-2.5 py-1 text-[11px] text-gray-600 transition-colors hover:border-gray-400 hover:text-gray-900"
                  >
                    <Icon className="h-3 w-3" />
                    {t(preset.labelKey as TranslationKey)}
                  </button>
                );
              })}

              {isAddingInterest ? (
                <input
                  autoFocus
                  value={customInterest}
                  onChange={(e) => setCustomInterest(e.target.value)}
                  onBlur={addCustomInterest}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ',') {
                      e.preventDefault();
                      addCustomInterest();
                    }
                    if (e.key === 'Escape') {
                      setCustomInterest('');
                      setIsAddingInterest(false);
                    }
                  }}
                  placeholder={String(t('clientRequest.trip.interestsPlaceholder'))}
                  className="w-32 rounded-full border border-gray-400 px-2.5 py-1 text-[11px] outline-none placeholder:text-gray-400"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setIsAddingInterest(true)}
                  className="inline-flex items-center gap-1 rounded-full border border-dashed border-gray-300 px-2.5 py-1 text-[11px] text-gray-500 transition-colors hover:border-gray-400 hover:text-gray-900"
                >
                  <Plus className="h-3 w-3" />
                  {t('clientRequest.trip.interestsOther')}
                </button>
              )}
            </div>

          </>
        )}
      </div>

    </div>
  );

  const notesTab = (
    <div className="flex h-full flex-col gap-1.5">
      <Label htmlFor="notes" className="text-xs text-gray-700">
        {t('clientRequest.notes.hint')}
      </Label>
      <Textarea
        id="notes"
        value={form.additionalNotes}
        onChange={(e) => patch({ additionalNotes: e.target.value })}
        placeholder={String(t('clientRequest.trip.notesPlaceholder'))}
        disabled={disabled}
        className="min-h-[18rem] flex-1 resize-none"
      />
    </div>
  );

  const assignmentTab = (
    <div className="space-y-5">
      <Field
        id="advisor"
        label={String(t('travelRequest.assignToLabel'))}
        icon={<Briefcase className="h-3 w-3" />}
        hint={isEdit ? String(t('clientRequest.assignment.reassignNotice')) : undefined}
      >
        <Select
          value={form.advisorId}
          onValueChange={(value) => patch({ advisorId: value })}
          disabled={disabled}
        >
          <SelectTrigger className="h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ADVISOR_SELF}>{t('travelRequest.agencyMyself')}</SelectItem>
            {advisors.map((advisor) => (
              <SelectItem key={advisor.id} value={advisor.id}>
                {advisor.first_name} {advisor.last_name} ({advisor.email})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>

      {isEdit && (
        <p className="text-xs text-gray-500">
          {t('clientRequest.assignment.statusNotice')}
        </p>
      )}
    </div>
  );

  const footer = (
    <div className="flex items-center justify-between gap-3">
      <div className="text-xs">
        {showErrors && errorCount > 0 && (
          <span className="text-red-600 flex items-center gap-1">
            <AlertCircle className="h-3.5 w-3.5" />
            {t('clientRequest.footer.errors', { count: errorCount })}
          </span>
        )}
      </div>
      <div className="flex gap-2">
        <Button variant="outline" onClick={handleClose} disabled={isLoading}>
          {readOnly ? t('common.close') : t('common.cancel')}
        </Button>
        {!readOnly && (
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading
              ? t('common.saving')
              : isEdit
                ? t('clientRequest.footer.save')
                : t('clientRequest.footer.create')}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEdit ? t('clientRequest.titleEdit') : t('clientRequest.titleCreate')}
      size="lg"
      footer={footer}
    >
      <div className="flex flex-col md:flex-row min-h-[26rem] max-h-[70vh]">
        {/* Navigazione: colonna a sinistra da tablet in su, riga scorrevole su mobile */}
        <nav className="flex md:flex-col gap-1 shrink-0 md:w-44 p-2 md:p-3 border-b md:border-b-0 md:border-r border-gray-200 overflow-x-auto">
          {tabs.map((tab, index) => (
            <Fragment key={tab.id}>
              {/* Riga di separazione davanti al primo tab facoltativo. */}
              {index === dividerAt && (
                <span
                  className="shrink-0 self-stretch border-l md:border-l-0 md:border-t border-gray-200 md:my-1.5 mx-1.5 md:mx-1"
                  aria-hidden
                />
              )}
              <button
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'flex items-center gap-2 rounded-md whitespace-nowrap transition-colors text-left',
                  // I tab senza campi obbligatori restano al loro posto ma in una
                  // taglia piu' piccola e in grigio.
                  REQUIRED_TABS.has(tab.id)
                    ? 'px-3 py-2 text-sm font-medium'
                    : 'px-3 py-1.5 text-xs font-normal',
                  activeTab === tab.id
                    ? 'bg-gray-900 text-white'
                    : REQUIRED_TABS.has(tab.id)
                      ? 'text-gray-600 hover:bg-gray-100'
                      : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600',
                )}
              >
                {tab.icon}
                <span className="flex-1">{tab.label}</span>
                {tabHasError(tab.id) && (
                  <span className="h-1.5 w-1.5 rounded-full bg-red-500 shrink-0" aria-hidden />
                )}
                {((tab.id === 'travellers' && hasTravellerData(form))
                  || (tab.id === 'notes' && !!form.additionalNotes.trim()))
                  && !tabHasError(tab.id) && (
                  <span
                    className={cn(
                      'h-1.5 w-1.5 rounded-full shrink-0',
                      activeTab === tab.id ? 'bg-white/70' : 'bg-gray-400',
                    )}
                    aria-hidden
                  />
                )}
              </button>
            </Fragment>
          ))}
        </nav>

        <div className="flex-1 overflow-y-auto p-5 md:p-6">
          {readOnly && (
            <div className="mb-5 flex items-start gap-2 rounded-md border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600">
              <Lock className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{t('clientRequest.readOnlyNotice')}</span>
            </div>
          )}
          {activeTab === 'client' && clientTab}
          {activeTab === 'trip' && tripTab}
          {activeTab === 'travellers' && travellersTab}
          {activeTab === 'details' && detailsTab}
          {activeTab === 'notes' && notesTab}
          {activeTab === 'assignment' && assignmentTab}
        </div>
      </div>
    </Modal>
  );
};

/** Alias storico: il modale nasce come "crea richiesta cliente". */
export const CreateClientRequestModal = ClientRequestModal;
